import { useEffect, useRef, useState } from 'react';
import {
  clampMetronomeBpm,
  METRONOME_BPM_MAX,
  METRONOME_BPM_MIN,
} from '../lib/presentationTempo';

interface MetronomeBpmPopoverProps {
  bpm: number;
  suggested: number;
  custom: boolean;
  onBpmChange: (bpm: number) => void;
  onUseSuggested: () => void;
}

export default function MetronomeBpmPopover({
  bpm,
  suggested,
  custom,
  onBpmChange,
  onUseSuggested,
}: MetronomeBpmPopoverProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const onClick = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onClick);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onClick);
    };
  }, [open]);

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        className="rounded-lg bg-white/80 px-2 py-1 text-xs font-medium text-indigo-800 ring-1 ring-indigo-200 transition hover:bg-white dark:bg-zinc-900/80 dark:text-indigo-200 dark:ring-indigo-500/40"
        aria-expanded={open}
        aria-label={`Tempo: ${bpm} BPM`}
      >
        {bpm} BPM ▾
      </button>
      {open && (
        <div
          className="absolute right-0 top-full z-10 mt-1 w-52 rounded-xl bg-white p-3 shadow-lg ring-1 ring-zinc-200 dark:bg-zinc-900 dark:ring-white/10"
          onClick={(e) => e.stopPropagation()}
        >
          <p className="text-xs font-medium text-strong">Tempo</p>
          <p className="mt-0.5 font-mono text-sm font-semibold tabular-nums text-indigo-600 dark:text-indigo-300">
            {bpm} BPM
          </p>
          <input
            type="range"
            min={METRONOME_BPM_MIN}
            max={METRONOME_BPM_MAX}
            step={1}
            value={bpm}
            onChange={(e) =>
              onBpmChange(clampMetronomeBpm(Number(e.target.value)))
            }
            className="mt-2 w-full accent-indigo-500"
            aria-label="Metronom-tempo i BPM"
          />
          <div className="mt-1 flex justify-between text-[10px] text-faint">
            <span>{METRONOME_BPM_MIN}</span>
            <span>{METRONOME_BPM_MAX}</span>
          </div>
          <p className="mt-2 text-[10px] text-muted">
            Anbefalet:{' '}
            <span className="font-mono font-medium text-strong">
              {suggested} BPM
            </span>
          </p>
          {custom && (
            <button
              type="button"
              onClick={() => {
                onUseSuggested();
                setOpen(false);
              }}
              className="mt-2 text-xs font-medium text-indigo-600 hover:text-indigo-500 dark:text-indigo-400"
            >
              Brug anbefalet
            </button>
          )}
        </div>
      )}
    </div>
  );
}
